import { useState } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, TrendingDown } from 'lucide-react';
import type { TodayFundraising } from '../../types';
import { pickWindow, seasonLabel, windowCaption, type FundMetric } from '../../lib/fundraisingWindow';
import { SegmentPills } from '../shared/SegmentPills';

const fmtUsd = (cents: string): string => {
  const dollars = Math.round(Number(cents) / 100);
  return `$${dollars.toLocaleString()}`;
};

/**
 * The rep's money — the headline for a salesperson. Total raised by their
 * accounts, with a YTD ⇄ Season toggle. YTD is the default (calendar year to
 * date). Reads from the new `windows` block via `pickWindow` (D1).
 */
export function FundraisingCard({ fundraising }: { fundraising: TodayFundraising }) {
  const [metric, setMetric] = useState<FundMetric>('calYtd');
  const w = pickWindow(fundraising.windows, metric, { season: fundraising.season });
  const caption = windowCaption(metric, fundraising.fundraisingWindows, fundraising.season);
  const hasAny = w.campaigns > 0 || w.raisedCents !== '0';

  return (
    <div className="card p-5 sm:p-6 bg-blast-50 dark:bg-blast-950/40 border-blast-200 dark:border-blast-900">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h3 className="text-[11px] font-semibold uppercase tracking-[0.08em] text-blast-700 dark:text-blast-300">
          Your fundraising · {w.label}
        </h3>
        <SegmentPills
          activeKey={metric}
          onSelect={(k) => setMetric(k as FundMetric)}
          pills={[
            { key: 'calYtd', label: 'YTD', count: null },
            { key: 'season', label: seasonLabel(fundraising.season.currentSeasonYear), count: null },
          ]}
        />
      </div>

      {caption && (
        <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">{caption}</p>
      )}

      <div className="mt-1.5 flex items-baseline gap-2 flex-wrap">
        <span className="text-3xl font-bold tabular-nums text-gray-900 dark:text-gray-50">
          {fmtUsd(w.raisedCents)}
        </span>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          raised · {w.campaigns} {w.campaigns === 1 ? 'campaign' : 'campaigns'}
        </span>
        {w.yoyPct !== null && (
          <span
            className={`inline-flex items-center gap-1 text-xs font-semibold ${
              w.yoyPct >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'
            }`}
          >
            {w.yoyPct >= 0 ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
            {w.yoyPct >= 0 ? '+' : ''}
            {Math.round(w.yoyPct)}% vs {w.priorLabel}
          </span>
        )}
      </div>

      {hasAny && fundraising.topCampaigns.length > 0 && (
        <div className="mt-4 space-y-1">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
            Top fundraisers
          </p>
          {fundraising.topCampaigns.map((c) => (
            <Link
              key={c.id}
              to={`/organizations/${c.organizationId}`}
              className="flex items-center justify-between gap-3 rounded-md px-1.5 py-1 -mx-1.5 hover:bg-white/60 dark:hover:bg-gray-800/40 transition-colors"
            >
              <span className="min-w-0 truncate text-sm text-gray-700 dark:text-gray-200">
                {c.organizationName || 'Unknown school'}
              </span>
              <span className="flex-shrink-0 text-sm font-semibold tabular-nums text-gray-900 dark:text-gray-100">
                {fmtUsd(c.raisedCents)}
              </span>
            </Link>
          ))}
        </div>
      )}

      {!hasAny && (
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
          No fundraisers credited to you {metric === 'calYtd' ? 'this year' : `in ${seasonLabel(fundraising.season.currentSeasonYear)}`} yet.
        </p>
      )}
    </div>
  );
}
